<script setup lang="ts">
import { Camera, ChevronRight } from "lucide-vue-next";
import type { PublicWorkerService } from "~/types/public-worker";
import { toTitleCase } from "~/utils/search-catalog";
import { formatMoneyAmount } from "~/utils/money";

const { service, showCategory = false } = defineProps<{
  service: PublicWorkerService;
  showCategory?: boolean;
}>();

const { locale, t } = useI18n();

const detailsOpen = ref(false);
const detailsInitialIndex = ref(0);
const descriptionElement = ref<HTMLElement | null>(null);
const descriptionIsLong = ref(false);

let descriptionResizeObserver: ResizeObserver | null = null;

const imageUrls = computed(() =>
  service.portfolioItems
    .map(item => item.photoUrl)
    .filter((image): image is string => Boolean(image)),
);

const translatedServiceName = computed(() =>
  catalogLabel("services", service.name),
);
const translatedCategoryName = computed(() =>
  catalogLabel("category", service.categoryName),
);

const descriptionText = computed(
  () =>
    service.description?.trim() || t("worker_profile.no_service_description"),
);

function pricingUnitSuffix(unit: string) {
  if (unit === "hour")
    return ` / ${t("worker_profile_page.services.pricing.unit_hour_short")}`;
  if (unit === "m2")
    return ` / ${t("worker_profile_page.services.pricing.unit_m2_short")}`;
  if (unit === "meter")
    return ` / ${t("worker_profile_page.services.pricing.unit_meter_short")}`;
  if (unit === "unit")
    return ` / ${t("worker_profile_page.services.pricing.unit_unit_short")}`;
  return "";
}

const formattedPrice = computed(() => {
  const { pricingType, pricingUnit, priceFrom, priceTo, priceCurrency } =
    service;
  const suffix = pricingUnitSuffix(pricingUnit);

  if (pricingType === "negotiable" || priceFrom == null) {
    return t("worker_profile.price_negotiable");
  }

  if (pricingType === "range" && priceTo != null) {
    return `${formatMoneyAmount(
      priceFrom,
      priceCurrency,
      locale.value,
    )} – ${formatMoneyAmount(priceTo, priceCurrency, locale.value)}${suffix}`;
  }

  if (pricingType === "from") {
    return `${t("worker_profile.from")} ${formatMoneyAmount(
      priceFrom,
      priceCurrency,
      locale.value,
    )}${suffix}`;
  }

  return `${formatMoneyAmount(priceFrom, priceCurrency, locale.value)}${suffix}`;
});

function catalogLabel(
  type: "category" | "services" | "search.card.pricing",
  value: string,
) {
  const key = `${type}.${value}`;
  const translated = t(key);

  return translated === key ? toTitleCase(value) : translated;
}

async function checkDescriptionOverflow() {
  await nextTick();

  const element = descriptionElement.value;

  if (!element) {
    descriptionIsLong.value = false;
    return;
  }

  descriptionIsLong.value = element.scrollHeight > element.clientHeight + 1;
}

function openDetails(index = 0) {
  detailsInitialIndex.value = Math.min(
    Math.max(index, 0),
    Math.max(imageUrls.value.length - 1, 0),
  );
  detailsOpen.value = true;
}

watch(descriptionText, async () => {
  descriptionIsLong.value = false;
  await checkDescriptionOverflow();
});

onMounted(async () => {
  await checkDescriptionOverflow();

  if (!descriptionElement.value) return;

  descriptionResizeObserver = new ResizeObserver(() => {
    void checkDescriptionOverflow();
  });

  descriptionResizeObserver.observe(descriptionElement.value);
});

onBeforeUnmount(() => {
  descriptionResizeObserver?.disconnect();
  descriptionResizeObserver = null;
});
</script>

<template>
  <article
    class="group flex min-w-0 flex-col overflow-hidden rounded-2xl border border-app-border bg-white transition duration-200 hover:border-primary/25 hover:shadow-sm"
  >
    <WorkerPublicServiceGallery
      :images="imageUrls"
      :alt="translatedServiceName"
      @open-details="openDetails"
    />

    <div class="flex flex-1 flex-col p-5">
      <div class="flex-1">
        <span
          v-if="showCategory"
          class="mb-3 inline-flex rounded-full bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary"
        >
          {{ translatedCategoryName }}
        </span>

        <div class="flex min-w-0 items-start justify-between gap-3">
          <h3 class="min-w-0 text-lg font-bold leading-6 text-app-text">
            {{ translatedServiceName }}
          </h3>

          <span
            v-if="imageUrls.length"
            class="inline-flex shrink-0 items-center gap-1.5 pt-0.5 text-xs font-medium text-app-muted"
          >
            <Camera class="size-3.5" />
            {{
              $t("worker_profile.photos_count", {
                count: imageUrls.length,
              })
            }}
          </span>
        </div>

        <strong
          class="mt-3 block min-w-0 break-words text-xl font-bold leading-tight text-primary"
        >
          {{ formattedPrice }}
        </strong>

        <p
          ref="descriptionElement"
          class="mt-4 line-clamp-3 whitespace-pre-wrap break-words text-sm leading-6 text-app-muted"
        >
          {{ descriptionText }}
        </p>

        <button
          v-if="descriptionIsLong"
          type="button"
          class="mt-3 inline-flex w-fit items-center gap-1 text-sm font-semibold text-primary transition hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25"
          @click="openDetails()"
        >
          {{ $t("worker_profile.show_more") }}
          <ChevronRight class="size-4" />
        </button>
      </div>
    </div>

    <WorkerPublicServiceDetailsModal
      v-model="detailsOpen"
      :images="imageUrls"
      :alt="translatedServiceName"
      :title="translatedServiceName"
      :price="formattedPrice"
      :description="descriptionText"
      :category="showCategory ? translatedCategoryName : undefined"
      :initial-index="detailsInitialIndex"
    />
  </article>
</template>
